import { useTranslation } from 'react-i18next';
import {
  DocumentationScopeNameEnum,
  useEdmDocumentationsIdsQuery,
} from 'graphql-common';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageIconColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';

interface DocumentDeleteLinkedWarningProps {
  ids: string[];
  isOpen: boolean;
  isViewLocked?: boolean;
}

function useScopeHasLinks(
  ids: string[],
  isOpen: boolean,
  scopeName: DocumentationScopeNameEnum,
  isViewLocked?: boolean,
) {
  // All `LinkedTo*` scopes are gated by `documentation_management` on the BE.
  // Locked users can't link docs to forms/tasks/areas, so there's nothing
  // meaningful to warn about — skip the query to avoid UPGRADE_PLAN_REQUIRED
  // noise on every delete-confirm open.
  const skip = !isOpen || ids.length === 0 || !!isViewLocked;
  const { data, loading } = useEdmDocumentationsIdsQuery({
    fetchPolicy: 'no-cache',
    skip,
    variables: {
      onlyIds: true,
      limit: 1,
      filters: [{ id: { predicate: { in: ids } } }],
      scope: { name: scopeName },
    },
  });
  return {
    loading: !skip && loading,
    hasLinks: (data?.data?.collection?.length ?? 0) > 0,
  };
}

export default function DocumentDeleteLinkedWarning({
  ids,
  isOpen,
  isViewLocked,
}: DocumentDeleteLinkedWarningProps) {
  const { t } = useTranslation();

  const forms = useScopeHasLinks(
    ids,
    isOpen,
    DocumentationScopeNameEnum.LinkedToForms,
    isViewLocked,
  );
  const tasks = useScopeHasLinks(
    ids,
    isOpen,
    DocumentationScopeNameEnum.LinkedToTasks,
    isViewLocked,
  );
  const assets = useScopeHasLinks(
    ids,
    isOpen,
    DocumentationScopeNameEnum.LinkedToAssets,
    isViewLocked,
  );
  const areas = useScopeHasLinks(
    ids,
    isOpen,
    DocumentationScopeNameEnum.LinkedToAreas,
    isViewLocked,
  );

  if (!isOpen || ids.length === 0) return null;
  if (forms.loading || tasks.loading || assets.loading || areas.loading) {
    return null;
  }

  const showFormsTasks = forms.hasLinks || tasks.hasLinks;
  const showEquipmentAreas = assets.hasLinks || areas.hasLinks;

  if (!showFormsTasks && !showEquipmentAreas) return null;

  let formsTasksLabel = '';
  if (forms.hasLinks && tasks.hasLinks) {
    formsTasksLabel = `${t('forms')} / ${t('tasks')}`;
  } else if (forms.hasLinks) {
    formsTasksLabel = t('forms');
  } else if (tasks.hasLinks) {
    formsTasksLabel = t('tasks');
  }

  let equipmentAreasLabel = '';
  if (assets.hasLinks && areas.hasLinks) {
    equipmentAreasLabel = `${t('assets')} / ${t('areas')}`;
  } else if (assets.hasLinks) {
    equipmentAreasLabel = t('assets');
  } else if (areas.hasLinks) {
    equipmentAreasLabel = t('areas');
  }

  return (
    <SupportiveMessage
      color={SupportiveMessageColor.Yellow}
      iconColor={SupportiveMessageIconColor.Inner}
      hasBottomSpace={false}
      text={
        <>
          <p>{t('documents-delete-linked-warning-intro')}</p>
          <ul>
            {showFormsTasks && (
              <li>
                <strong>{formsTasksLabel}</strong>
                <br /> {t('documents-delete-linked-forms-tasks-text')}
              </li>
            )}
            {showEquipmentAreas && (
              <li>
                <strong>{equipmentAreasLabel}</strong>
                <br /> {t('documents-delete-linked-equipment-areas-text')}
              </li>
            )}
          </ul>
        </>
      }
    />
  );
}
